import React from 'react';
import {
  ArrowRight,
  BarChart2,
  Bell,
  Building2,
  Check,
  FileText,
  Megaphone,
  Settings,
  Shield,
  TrendingUp,
  Users,
  Wrench,
  Zap
} from 'lucide-react';
import Footer from './Footer';

const ManagersPage = ({ showPage }) => {
  return (
    <div className="page active">
      {/* HERO SECTION */}
      <section className="pt-28 pb-20 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-semibold mb-6">
              <Building2 className="w-3 h-3" /> For Property Managers
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white mb-6">
              Manage smarter.
            </h1>
            <p className="text-xl text-neutral-300 leading-relaxed mb-10">
              A powerful dashboard to manage rent collection, electricity distribution, maintenance, and residents — all in one place.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => window.location.href = 'https://admin.bilm.app/auth/sign-up'}
                className="inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold rounded-small text-neutral-900 bg-white hover:bg-neutral-100 shadow-lg transition-all gap-2"
              >
                Get Started Free <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="mailto:sales@bilm.app"
                className="inline-flex items-center justify-center px-7 py-3.5 border border-white/30 text-base font-semibold rounded-small text-white hover:bg-white/10 transition-all gap-2 backdrop-blur-sm"
              >
                Book a Demo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ELECTRICITY MANAGEMENT SECTION */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold mb-4">
              <Zap className="w-3 h-3" /> Electricity Management
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">Full visibility over electricity usage</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Monitor electricity consumption across all units, track purchases, set usage alerts, and manage meter configurations.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-large shadow-custom p-6 border border-amber-100">
              <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-large flex items-center justify-center mb-4">
                <BarChart2 className="w-6 h-6" />
              </div>
              <h3 className="font-heading font-bold text-neutral-900 mb-2">Usage Analytics</h3>
              <p className="text-neutral-600 text-sm">View electricity consumption per unit and identify high-consumption units.</p>
            </div>
            <div className="bg-white rounded-large shadow-custom p-6 border border-amber-100">
              <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-large flex items-center justify-center mb-4">
                <Settings className="w-6 h-6" />
              </div>
              <h3 className="font-heading font-bold text-neutral-900 mb-2">Meter Management</h3>
              <p className="text-neutral-600 text-sm">Register and manage meter numbers for all units automatically on move-in.</p>
            </div>
            <div className="bg-white rounded-large shadow-custom p-6 border border-amber-100">
              <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-large flex items-center justify-center mb-4">
                <Bell className="w-6 h-6" />
              </div>
              <h3 className="font-heading font-bold text-neutral-900 mb-2">Low Balance Alerts</h3>
              <p className="text-neutral-600 text-sm">Get notified when any unit's electricity balance drops below a threshold.</p>
            </div>
          </div>
          <div className="bg-white rounded-large shadow-custom-hover border border-neutral-100 overflow-hidden">
            <div className="bg-neutral-900 px-6 py-4 flex items-center justify-between">
              <h3 className="font-heading font-bold text-white">Electricity Management Dashboard</h3>
              <div className="flex items-center gap-2 text-xs text-neutral-400">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                Live
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-amber-50 rounded-large p-4 border border-amber-100">
                  <div className="text-xs font-semibold text-amber-600 mb-1 uppercase tracking-wide">Total Units</div>
                  <div className="font-heading text-2xl font-bold text-neutral-900">48</div>
                  <div className="text-xs text-neutral-500 mt-1">Active meters</div>
                </div>
                <div className="bg-green-50 rounded-large p-4 border border-green-100">
                  <div className="text-xs font-semibold text-green-600 mb-1 uppercase tracking-wide">Purchases Today</div>
                  <div className="font-heading text-2xl font-bold text-neutral-900">12</div>
                  <div className="text-xs text-neutral-500 mt-1">₦48,500 total</div>
                </div>
                <div className="bg-red-50 rounded-large p-4 border border-red-100">
                  <div className="text-xs font-semibold text-red-600 mb-1 uppercase tracking-wide">Low Balance</div>
                  <div className="font-heading text-2xl font-bold text-neutral-900">3</div>
                  <div className="text-xs text-neutral-500 mt-1">Units below 10 kWh</div>
                </div>
                <div className="bg-blue-50 rounded-large p-4 border border-blue-100">
                  <div className="text-xs font-semibold text-blue-600 mb-1 uppercase tracking-wide">Monthly Revenue</div>
                  <div className="font-heading text-2xl font-bold text-neutral-900">₦1.2M</div>
                  <div className="text-xs text-neutral-500 mt-1">Electricity purchases</div>
                </div>
              </div>
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-heading font-bold text-neutral-900">Unit Electricity Status</h4>
                  <button className="text-xs text-primary-600 font-semibold">View All Units</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-neutral-200">
                        <th className="text-left py-3 px-3 text-xs font-semibold text-neutral-500 uppercase">Unit</th>
                        <th className="text-left py-3 px-3 text-xs font-semibold text-neutral-500 uppercase">Resident</th>
                        <th className="text-left py-3 px-3 text-xs font-semibold text-neutral-500 uppercase">Meter No.</th>
                        <th className="text-left py-3 px-3 text-xs font-semibold text-neutral-500 uppercase">Balance</th>
                        <th className="text-left py-3 px-3 text-xs font-semibold text-neutral-500 uppercase">Last Purchase</th>
                        <th className="text-left py-3 px-3 text-xs font-semibold text-neutral-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      <tr className="hover:bg-neutral-50">
                        <td className="py-3 px-3 font-semibold text-neutral-900">Unit 101</td>
                        <td className="py-3 px-3 text-neutral-600">Chidi Okafor</td>
                        <td className="py-3 px-3 text-neutral-500 font-mono text-xs">45110001234</td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-neutral-200 rounded-full h-1.5">
                              <div className="bg-green-500 h-1.5 rounded-full" style={{width: '75%'}}></div>
                            </div>
                            <span className="text-xs font-semibold text-neutral-700">75 kWh</span>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-neutral-500 text-xs">2 days ago</td>
                        <td className="py-3 px-3">
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Good</span>
                        </td>
                      </tr>
                      <tr className="hover:bg-neutral-50">
                        <td className="py-3 px-3 font-semibold text-neutral-900">Unit 204</td>
                        <td className="py-3 px-3 text-neutral-600">Amaka Eze</td>
                        <td className="py-3 px-3 text-neutral-500 font-mono text-xs">45110002345</td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-neutral-200 rounded-full h-1.5">
                              <div className="bg-amber-500 h-1.5 rounded-full" style={{width: '30%'}}></div>
                            </div>
                            <span className="text-xs font-semibold text-neutral-700">28 kWh</span>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-neutral-500 text-xs">5 days ago</td>
                        <td className="py-3 px-3">
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">Low</span>
                        </td>
                      </tr>
                      <tr className="hover:bg-neutral-50 bg-red-50/30">
                        <td className="py-3 px-3 font-semibold text-neutral-900">Unit 305</td>
                        <td className="py-3 px-3 text-neutral-600">Tunde Adeyemi</td>
                        <td className="py-3 px-3 text-neutral-500 font-mono text-xs">45110003456</td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-neutral-200 rounded-full h-1.5">
                              <div className="bg-red-500 h-1.5 rounded-full" style={{width: '8%'}}></div>
                            </div>
                            <span className="text-xs font-semibold text-red-600">7 kWh</span>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-neutral-500 text-xs">8 days ago</td>
                        <td className="py-3 px-3">
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded-full">Critical</span>
                        </td>
                      </tr>
                      <tr className="hover:bg-neutral-50">
                        <td className="py-3 px-3 font-semibold text-neutral-900">Unit 402</td>
                        <td className="py-3 px-3 text-neutral-600">Alex Johnson</td>
                        <td className="py-3 px-3 text-neutral-500 font-mono text-xs">45110012345</td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-neutral-200 rounded-full h-1.5">
                              <div className="bg-green-500 h-1.5 rounded-full" style={{width: '45%'}}></div>
                            </div>
                            <span className="text-xs font-semibold text-neutral-700">42 kWh</span>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-neutral-500 text-xs">Today</td>
                        <td className="py-3 px-3">
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Good</span>
                        </td>
                      </tr>
                      <tr className="hover:bg-neutral-50 bg-red-50/30">
                        <td className="py-3 px-3 font-semibold text-neutral-900">Unit 501</td>
                        <td className="py-3 px-3 text-neutral-600">Ngozi Obi</td>
                        <td className="py-3 px-3 text-neutral-500 font-mono text-xs">45110004567</td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-neutral-200 rounded-full h-1.5">
                              <div className="bg-red-500 h-1.5 rounded-full" style={{width: '5%'}}></div>
                            </div>
                            <span className="text-xs font-semibold text-red-600">4 kWh</span>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-neutral-500 text-xs">10 days ago</td>
                        <td className="py-3 px-3">
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded-full">Critical</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h4 className="font-heading font-bold text-neutral-900 mb-4">Monthly Electricity Purchases (₦)</h4>
                <div className="flex items-end gap-3 h-32">
                  <div className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-amber-200 rounded-t-small" style={{height: '45%'}}></div>
                    <span className="text-xs text-neutral-400">Jun</span>
                  </div>
                  <div className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-amber-300 rounded-t-small" style={{height: '60%'}}></div>
                    <span className="text-xs text-neutral-400">Jul</span>
                  </div>
                  <div className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-amber-400 rounded-t-small" style={{height: '55%'}}></div>
                    <span className="text-xs text-neutral-400">Aug</span>
                  </div>
                  <div className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-amber-400 rounded-t-small" style={{height: '70%'}}></div>
                    <span className="text-xs text-neutral-400">Sep</span>
                  </div>
                  <div className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-amber-500 rounded-t-small" style={{height: '80%'}}></div>
                    <span className="text-xs text-neutral-400">Oct</span>
                  </div>
                  <div className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-amber-600 rounded-t-small" style={{height: '100%'}}></div>
                    <span className="text-xs text-neutral-400">Nov</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPLETE PROPERTY MANAGEMENT SECTION */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-bold text-neutral-900 mb-12 text-center">Complete property management</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="p-6 rounded-large bg-background-50 border border-neutral-100 hover:shadow-custom transition-all">
              <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-large flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6" />
              </div>
              <h3 className="font-heading font-bold text-neutral-900 mb-2">Revenue Dashboard</h3>
              <p className="text-neutral-600 text-sm">Track rent collection, electricity revenue, and outstanding balances in real time.</p>
            </div>
            <div className="p-6 rounded-large bg-background-50 border border-neutral-100 hover:shadow-custom transition-all">
              <div className="w-12 h-12 bg-green-100 text-green-600 rounded-large flex items-center justify-center mb-4">
                <Users className="w-6 h-6" />
              </div>
              <h3 className="font-heading font-bold text-neutral-900 mb-2">Resident Management</h3>
              <p className="text-neutral-600 text-sm">Onboard residents, manage leases, and communicate with tenants directly.</p>
            </div>
            <div className="p-6 rounded-large bg-background-50 border border-neutral-100 hover:shadow-custom transition-all">
              <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-large flex items-center justify-center mb-4">
                <Wrench className="w-6 h-6" />
              </div>
              <h3 className="font-heading font-bold text-neutral-900 mb-2">Maintenance Oversight</h3>
              <p className="text-neutral-600 text-sm">Assign technicians, track repair progress, and maintain full maintenance history.</p>
            </div>
            <div className="p-6 rounded-large bg-background-50 border border-neutral-100 hover:shadow-custom transition-all">
              <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-large flex items-center justify-center mb-4">
                <FileText className="w-6 h-6" />
              </div>
              <h3 className="font-heading font-bold text-neutral-900 mb-2">Reports & Exports</h3>
              <p className="text-neutral-600 text-sm">Generate detailed reports on rent, electricity, and maintenance. Export to PDF or Excel.</p>
            </div>
            <div className="p-6 rounded-large bg-background-50 border border-neutral-100 hover:shadow-custom transition-all">
              <div className="w-12 h-12 bg-rose-100 text-rose-600 rounded-large flex items-center justify-center mb-4">
                <Megaphone className="w-6 h-6" />
              </div>
              <h3 className="font-heading font-bold text-neutral-900 mb-2">Announcements</h3>
              <p className="text-neutral-600 text-sm">Send building-wide or unit-specific announcements instantly via push notification.</p>
            </div>
            <div className="p-6 rounded-large bg-background-50 border border-neutral-100 hover:shadow-custom transition-all">
              <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-large flex items-center justify-center mb-4">
                <Shield className="w-6 h-6" />
              </div>
              <h3 className="font-heading font-bold text-neutral-900 mb-2">Access Control</h3>
              <p className="text-neutral-600 text-sm">Manage building access, visitor logs, and security alerts from a central dashboard.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section className="py-20 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">Simple, transparent pricing</h2>
          <p className="text-neutral-400 mb-12 max-w-xl mx-auto">All plans include electricity management. Scale as your portfolio grows.</p>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-neutral-800 rounded-large p-8 border border-neutral-700 text-left">
              <h3 className="font-heading font-bold text-white text-xl mb-2">Starter</h3>
              <div className="font-heading text-4xl font-extrabold text-white mb-1">
                ₦25,000<span className="text-lg text-neutral-400 font-normal">/mo</span>
              </div>
              <p className="text-neutral-400 text-sm mb-6">Up to 20 units</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-400" />
                  Rent collection
                </li>
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-400" />
                  Electricity management
                </li>
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-400" />
                  Maintenance tracking
                </li>
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-400" />
                  Basic reports
                </li>
              </ul>
              <button className="w-full py-3 bg-neutral-700 text-white font-semibold rounded-small hover:bg-neutral-600 transition-colors">
                Get Started
              </button>
            </div>
            <div className="bg-primary-600 rounded-large p-8 border border-primary-500 text-left relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-neutral-900 text-xs font-bold px-3 py-1 rounded-full">
                Most Popular
              </div>
              <h3 className="font-heading font-bold text-white text-xl mb-2">Growth</h3>
              <div className="font-heading text-4xl font-extrabold text-white mb-1">
                ₦65,000<span className="text-lg text-primary-200 font-normal">/mo</span>
              </div>
              <p className="text-primary-200 text-sm mb-6">Up to 100 units</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-sm text-white">
                  <Check className="w-4 h-4 text-amber-300" />
                  Everything in Starter
                </li>
                <li className="flex items-center gap-2 text-sm text-white">
                  <Check className="w-4 h-4 text-amber-300" />
                  Advanced electricity analytics
                </li>
                <li className="flex items-center gap-2 text-sm text-white">
                  <Check className="w-4 h-4 text-amber-300" />
                  Bulk meter management
                </li>
                <li className="flex items-center gap-2 text-sm text-white">
                  <Check className="w-4 h-4 text-amber-300" />
                  Priority support
                </li>
              </ul>
              <button className="w-full py-3 bg-amber-400 text-neutral-900 font-bold rounded-small hover:bg-amber-300 transition-colors">
                Start Free Trial
              </button>
            </div>
            <div className="bg-neutral-800 rounded-large p-8 border border-neutral-700 text-left">
              <h3 className="font-heading font-bold text-white text-xl mb-2">Enterprise</h3>
              <div className="font-heading text-4xl font-extrabold text-white mb-1">Custom</div>
              <p className="text-neutral-400 text-sm mb-6">Unlimited units</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-400" />
                  Everything in Growth
                </li>
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-400" />
                  Custom integrations
                </li>
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-400" />
                  Dedicated account manager
                </li>
                <li className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-green-400" />
                  SLA guarantee
                </li>
              </ul>
              <button className="w-full py-3 border border-neutral-600 text-white font-semibold rounded-small hover:bg-neutral-700 transition-colors">
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1A2A6C 0%, #2D52B8 50%, #4F8EF7 100%)' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">Ready to simplify your operations?</h2>
          <p className="text-blue-100 text-lg mb-10 max-w-xl mx-auto">Join 1,200+ properties already using Bilm to automate rent collection, electricity management, and more.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.location.href = 'https://admin.bilm.app/auth/sign-up'}
              className="inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold rounded-small text-primary-900 bg-white hover:bg-blue-50 shadow-lg transition-all gap-2"
            >
              Get Started Free <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href="mailto:sales@bilm.app"
              className="inline-flex items-center justify-center px-7 py-3.5 border border-white/30 text-base font-semibold rounded-small text-white hover:bg-white/10 transition-all gap-2 backdrop-blur-sm"
            >
              Book a Demo
            </a>
          </div>
        </div>
      </section>

      <Footer showPage={showPage} />
    </div>
  );
};

export default ManagersPage;
